import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AxiosError } from 'axios'
import { TaskService } from '@duck/api-definitions'
import {
  Breadcrumbs,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@duck/ui'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'
import { useTaskValidation } from '@/hooks/useTaskValidation'
import { useAdminTask } from '@/hooks/useAdminTask'
import { useAdminTasks } from '@/hooks/useAdminTasks'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { StandardLoader } from '@/components/StandardLoader'
import { NoDataEmptyState } from '@/components/EmptyStates'
import { DetailsConfigSection } from '../DetailsConfigSection'
import { DataSection } from '../DataSection'
import { PayloadSection } from '../PayloadSection'
import { BodySection } from '../BodySection'
import { ActionsSection } from '../ActionsSection'

const BLANK_TASK: Partial<TaskService.TaskBase> = { allowedWeapons: ['SQL'] }

interface TaskDetailPageProps {
  id: string
}

export const TaskDetailPage: React.FC<TaskDetailPageProps> = ({ id }) => {
  const { t } = useTar()
  const navigate = useNavigate()
  const isCreateMode = id === 'new'

  const { task, isLoading, isError, notFound, refetch } = useAdminTask(id)

  useDocumentTitle(isCreateMode ? 'New Task' : task?.name ?? id, 'admin')

  // Derive access groups from the tasks list (same approach as UserDetailPage).
  const { tasks } = useAdminTasks({ select: ['accessGroups'] })
  const allAccessGroups = useMemo(
    () => tasks.flatMap((tk) => tk.accessGroups),
    [tasks],
  )

  const [formData, setFormData] = useState<Partial<TaskService.TaskBase>>(BLANK_TASK)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [createMore, setCreateMore] = useState(false)
  const { validateTask, clearErrors, fieldErrors } = useTaskValidation()

  // Sync form state with the fetched task (edit) or blank defaults (create).
  useEffect(() => {
    if (isCreateMode) {
      setFormData(BLANK_TASK)
    } else if (task) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: _id, ...taskData } = task
      setFormData({ ...taskData, allowedWeapons: taskData.allowedWeapons ?? ['SQL'] })
    }
    setSaveError(null)
    setIsSaving(false)
    clearErrors()
  }, [task, isCreateMode, clearErrors])

  const handleInputChange = (
    field: keyof TaskService.Task,
    value: string | number | string[] | TaskService.TaskWeaponsOfChoiceEnum[] | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setSaveError(null)

      const validation = validateTask(formData, isCreateMode)
      if (!validation.isValid) {
        const errorMessages = Object.entries(validation.errors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join('; ')
        setSaveError(`Validation failed: ${errorMessages}`)
        return
      }

      if (isCreateMode) {
        const response = await apis.taskService.rootPost(formData as TaskService.TaskBase)
        toast.success(t('tasks.taskCreated'))
        if (createMore) {
          setFormData(BLANK_TASK)
          clearErrors()
        } else {
          void navigate({ to: '/admin/tasks/$id', params: { id: response.data.id } })
        }
      } else {
        await apis.taskService.idPut(id, formData as TaskService.TaskBase)
        toast.success(t('tasks.taskUpdated'))
        void refetch()
      }
    } catch (error) {
      console.error('Failed to save task:', error)
      setSaveError(
        error instanceof AxiosError
          ? error?.response?.data?.error
          : 'Failed to save task. Please try again.',
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (isCreateMode) return
    try {
      setIsSaving(true)
      setSaveError(null)
      await apis.taskService.idDelete(id)
      void navigate({ to: '/admin/tasks' })
    } catch (error) {
      console.error('Failed to delete task:', error)
      setSaveError(
        error instanceof AxiosError
          ? error?.response?.data?.error
          : 'Failed to delete task. Please try again.',
      )
    } finally {
      setIsSaving(false)
    }
  }

  const title = isCreateMode ? t('tasks.newTask') : task?.name ?? id

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('challenges.breadcrumbAdmin'), href: '/admin' },
          { label: t('navigation.tasks'), href: '/admin/tasks' },
          { label: title },
        ]}
        renderLink={(item, children) => <Link to={item.href}>{children}</Link>}
      />

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <StandardLoader />
        </div>
      )}

      {isError && !isLoading && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">{t('common.error')}</div>
        </div>
      )}

      {notFound && !isLoading && (
        <NoDataEmptyState
          title={t('tasks.detailNotFound')}
          buttonText={t('navigation.tasks')}
          onButtonClick={() => { void navigate({ to: '/admin/tasks' }) }}
        />
      )}

      {!isLoading && !isError && !notFound && (
        <>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-foreground">{title}</h2>
            <div className="flex items-center gap-4">
              {isCreateMode && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                  <Switch
                    checked={createMore}
                    onCheckedChange={setCreateMore}
                    disabled={isSaving}
                  />
                  {t('tasks.createMore')}
                </label>
              )}
              <button
                data-testid="task-detail-save"
                onClick={() => { void handleSave() }}
                disabled={isSaving}
                className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSaving && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {isSaving ? t('common.saving') : isCreateMode ? t('common.create') : t('common.save')}
              </button>
            </div>
          </div>

          <Tabs defaultValue="details" className="w-full">
            <TabsList className={`grid w-full ${isCreateMode ? 'grid-cols-4' : 'grid-cols-5'}`}>
              <TabsTrigger value="details">{t('common.details')}</TabsTrigger>
              <TabsTrigger value="data">{t('common.data')}</TabsTrigger>
              <TabsTrigger value="payload">{t('tasks.payload')}</TabsTrigger>
              <TabsTrigger value="body">{t('common.body')}</TabsTrigger>
              {!isCreateMode && <TabsTrigger value="actions">{t('common.actions')}</TabsTrigger>}
            </TabsList>

            <TabsContent value="details" className="mt-4">
              <DetailsConfigSection
                formData={formData}
                onChange={handleInputChange}
                fieldErrors={fieldErrors}
                allAccessGroups={allAccessGroups}
              />
            </TabsContent>

            <TabsContent value="data" className="mt-4">
              <DataSection formData={formData} onChange={handleInputChange} fieldErrors={fieldErrors} />
            </TabsContent>

            <TabsContent value="payload" className="mt-4">
              <PayloadSection formData={formData} onChange={handleInputChange} fieldErrors={fieldErrors} />
            </TabsContent>

            <TabsContent value="body" className="mt-4">
              <BodySection formData={formData} onChange={handleInputChange} isSaving={isSaving} fieldErrors={fieldErrors} />
            </TabsContent>

            {!isCreateMode && task && (
              <TabsContent value="actions" className="mt-4">
                <ActionsSection
                  taskId={task.id}
                  taskName={task.name}
                  currentSharedDBName={task.sharedDBName}
                  onDelete={() => { void handleDelete() }}
                  isSaving={isSaving}
                />
              </TabsContent>
            )}
          </Tabs>

          {saveError && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-md">
              <p className="text-sm text-error">{saveError}</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
